import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Phone, User, Clock, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CallbackForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    serviceInterest: '',
    preferredTime: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = [
    'Holiday Packages',
    'Hajj/Umrah',
    'International Visas',
    'Air/Bus Ticketing',
    'Travel Insurance',
    'Local Visa Services',
    'Cargo Services',
    'Airport Transfers',
    'Other',
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.phoneNumber) {
      toast.error('Required Fields Missing', {
        description: 'Please fill in your name and phone number.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/callback-request`,
        {
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,
          serviceInterest: formData.serviceInterest || null,
          preferredTime: formData.preferredTime || null,
        }
      );

      if (response.data.success) {
        toast.success('Request Submitted Successfully!', {
          description: 'We will call you back shortly. Thank you!',
        });
        
        // Reset form
        setFormData({
          fullName: '',
          phoneNumber: '',
          serviceInterest: '',
          preferredTime: '',
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      const errorMessage = error.response?.data?.detail || 'Failed to submit request. Please try again.';
      
      toast.error('Submission Failed', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="callback-form" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Request a <span className="text-[#1E7B8C]">Call Back</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Share your details and we'll reach out to help plan your perfect journey
            </p>
          </div>

          <Card className="border-0 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-[#1E7B8C] to-[#2A9DAF] text-white rounded-t-lg">
              <CardTitle className="text-2xl font-bold text-center">
                Get in Touch with Our Travel Experts
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-gray-700 font-semibold flex items-center">
                    <User className="h-4 w-4 mr-2 text-[#1E7B8C]" />
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="border-gray-300 focus:border-[#1E7B8C] focus:ring-[#1E7B8C] h-12"
                    required
                  />
                </div>

                {/* Phone Number */}
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber" className="text-gray-700 font-semibold flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-[#1E7B8C]" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    placeholder="+968 XXXX XXXX"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    className="border-gray-300 focus:border-[#1E7B8C] focus:ring-[#1E7B8C] h-12"
                    required
                  />
                </div>

                {/* Service Interest */}
                <div className="space-y-2">
                  <Label htmlFor="serviceInterest" className="text-gray-700 font-semibold flex items-center">
                    <Briefcase className="h-4 w-4 mr-2 text-[#1E7B8C]" />
                    Service of Interest (Optional)
                  </Label>
                  <Select
                    value={formData.serviceInterest}
                    onValueChange={(value) => setFormData({ ...formData, serviceInterest: value })}
                  >
                    <SelectTrigger className="border-gray-300 focus:border-[#1E7B8C] focus:ring-[#1E7B8C] h-12">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preferred Time */}
                <div className="space-y-2">
                  <Label htmlFor="preferredTime" className="text-gray-700 font-semibold flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-[#1E7B8C]" />
                    Preferred Time to Call (Optional)
                  </Label>
                  <Input
                    id="preferredTime"
                    type="text"
                    placeholder="e.g., Morning, Afternoon, Evening"
                    value={formData.preferredTime}
                    onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                    className="border-gray-300 focus:border-[#1E7B8C] focus:ring-[#1E7B8C] h-12"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#F4B41A] hover:bg-[#E5A615] text-white font-bold py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <span className="animate-spin mr-2">⏳</span>
                      Submitting...
                    </span>
                  ) : (
                    'Submit Request'
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center mt-4">
                  * Required fields. We respect your privacy and will never share your information.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default CallbackForm;